'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import type { ActivityItem } from '@/lib/api/history/models';
import { GlowName } from '@/lib/components/RewardsHub/GlowName';
import { Link } from '@/lib/routing';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/lib/ui-kit/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { parseUtcIso } from '@/lib/utils/dates';
import { Spinner } from '../ui-kit/spinner';
import { Empty } from './Empty';
import { RelativeDateText } from './Text/RelativeDateText';

interface ActivityTableProps {
  items: ActivityItem[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const ActivityTable = ({
  items,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: ActivityTableProps) => {
  const t = useTranslations('activity');
  const tCommon = useTranslations('common');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size={36} className="!text-v2-text-secondary" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Empty title={t('empty.title')} desc={t('empty.description')} />
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="border-v2-border hover:bg-transparent">
            <TableHead className="h-auto w-[220px] min-w-[220px] pb-2 pt-0 !text-[10px]">
              {t('table.user')}
            </TableHead>
            <TableHead className="hidden h-auto w-[220px] min-w-[220px] pb-2 pt-0 !text-[10px] lg:table-cell">
              {t('table.candidate')}
            </TableHead>
            <TableHead className="h-auto min-w-[50px] w-[100px] pb-2 pt-0 !text-[10px]">
              {t('table.side')}
            </TableHead>
            <TableHead className="h-auto w-[100px] min-w-[70px] pb-2 pt-0 !text-[10px]">
              {t('table.position')}
            </TableHead>
            <TableHead className="h-auto w-[100px] min-w-[60px] pb-2 pt-0 !text-[10px]">
              {t('shares')}
            </TableHead>
            <TableHead className="h-auto w-[80px] w-[100px] min-w-[80px] pb-2 pt-0 !text-[10px] text-right">
              {tCommon('time')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const isYes = item.position?.toLowerCase() === 'yes';
            return (
              <TableRow key={item.transactionId} className="border-b-0 hover:bg-transparent">
                <TableCell className="whitespace-normal py-3">
                  <Link
                    href={`/stats/profile/${item.userId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80"
                  >
                    <UserAvatar
                      userId={item.userId}
                      name={item.username}
                      look={item.look}
                      size={36}
                      className="shrink-0 border border-solid border-v2-border"
                    />
                    <span className="line-clamp-2 max-w-[160px] break-all text-[13px] font-medium leading-4 tracking-[-0.13px] text-v2-text-primary hover:underline">
                      <GlowName glow={item.look?.glow ?? null}>{item.username}</GlowName>
                    </span>
                  </Link>
                </TableCell>

                <TableCell className="hidden py-3 lg:table-cell">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="max-w-[220px] cursor-pointer truncate text-[13px] text-v2-text-primary">
                          {item.outcomeName}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>{item.outcomeName}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>

                <TableCell className="py-3 text-[13px] capitalize text-v2-text-primary">
                  {item.side?.toLowerCase()}
                </TableCell>

                <TableCell className="py-3">
                  <span
                    className={cn(
                      'flex w-[40px] items-center justify-center overflow-clip rounded-[8px] px-2 py-[2px] text-[12px] font-normal leading-4 tracking-[-0.12px]',
                      isYes
                        ? 'bg-v2-green-surface text-v2-green'
                        : 'bg-v2-red-surface text-red-400',
                    )}
                  >
                    {isYes ? tCommon('yes') : tCommon('no')}
                  </span>
                </TableCell>

                <TableCell className="py-3 text-[13px] text-v2-text-primary">
                  {item.shares.toLocaleString()}
                </TableCell>

                <TableCell className="py-3 text-right text-[13px] text-v2-text-primary">
                  <RelativeDateText date={parseUtcIso(item.executedAt)} formatStyle="compact" />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div ref={loadMoreRef} className="h-1" />
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Spinner size={24} />
        </div>
      )}
    </>
  );
};
